import { AppParams } from '../../types/internal-types';
import {
  ErrorReporterService,
  SentryAdapter,
} from '@wix/premium-bricks-services';

export interface SentryLoggerParams {
  context: 'server' | 'client' | 'test';
  appParams: Partial<AppParams>;
  additionalTags?: Object;
  Sentry: any;
  props?: Object;
  dsn?: string;
}

const sentryTags: Partial<keyof AppParams>[] = [
  'currencyCode',
  'country',
  'hasGoogleVoucher',
  'locale',
  'userGuid',
  'verticalName',
  'version',
  'isAnonymous',
  'purchaseMode',
  'siteGuid',
  'isUpgrade',
];
const sentryExtras: Partial<keyof AppParams>[] = [
  'products',
  'saleParams',
  'currentPlanDetails',
];

export class SentryLogger {
  private static reporter;
  private static SentryInstance;
  private static currentScope;

  static initSentry(loggerParams: SentryLoggerParams) {
    const { appParams, Sentry, props } = loggerParams;
    SentryLogger.SentryInstance = Sentry;
    const { version } = appParams;
    Sentry.init({
      release: `premium-purchase-plan@${version}`,
      attachStacktrace: true,
      ...(props || {}),
      dsn: 'https://95f8c02d881a48ada814a6d3b4461e31@sentry-next.wixpress.com/9209',
    });
    SentryLogger.configureScope(loggerParams);
    SentryLogger.setReporter(loggerParams);
  }

  static configureScope(loggerParams: SentryLoggerParams) {
    const { Sentry, appParams, context, additionalTags } = loggerParams;
    let { username } = appParams;
    if (!username) {
      const randomNumber = Math.floor(Math.random() * Math.floor(1000000));
      username = `anonymous_${randomNumber}`;
    }

    Sentry.configureScope((scope) => {
      SentryLogger.currentScope = scope;
      scope.setUser({ username });
      scope.setTag('context', context);
      if (appParams) {
        Object.entries(appParams)
          .filter(([key]) => sentryTags.includes(key as keyof AppParams))
          .forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        Object.entries(appParams)
          .filter(([key]) => sentryExtras.includes(key as keyof AppParams))
          .forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
      }
      if (additionalTags) {
        Object.entries(additionalTags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
    });
  }

  static setReporter(loggerParams: SentryLoggerParams) {
    const { appParams, Sentry } = loggerParams;
    const { debug } = appParams;
    SentryLogger.reporter = new ErrorReporterService(
      new SentryAdapter(Sentry.getCurrentHub()),
      debug,
    );
  }

  static logError(error: any) {
    SentryLogger.reporter?.reportError(
      error.message || JSON.stringify(error, null, 2),
    );
  }

  static logMessage(message: string) {
    SentryLogger.reporter.reportMessage(message);
  }

  static logAppCrash() {
    SentryLogger.SentryInstance.withScope((scope) => {
      scope.setLevel('fatal');
      SentryLogger.logError(new Error('Package Picker failed to load'));
    });
  }

  static addOrderSessionResponseToExtras(orderSessionResponse: any) {
    SentryLogger.currentScope?.setExtra(
      'orderSessionResponse',
      orderSessionResponse,
    );
  }
}
